import {
  MemberAddResponse,
  MemberInvitationBody,
  MemberRaw,
  MemberUpdateForm,
  getMemberUserId,
} from "../../domain/Member";
import { API_CLIENT } from "../config";
import { downloadFileFromAPI, getAPIConfigs } from "../helpers";

const apiUrlMembers = "/admin/members";
const apiUrlInvitedMembers = "/admin/invitations/members";

export const getAdminMembersCsv = async ({
  organizationId,
  organizationName,
  is_aa_constituent,
}: {
  organizationId: string;
  organizationName: string;
  is_aa_constituent: boolean;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const apiParams = {
    ...apiOrganizationIdParams,
    params: {
      ...apiOrganizationIdParams?.params,
      is_aa_constituent,
    },
  };
  const fileName = `${organizationName}_members${is_aa_constituent ? "_aa" : ""}`;
  await downloadFileFromAPI(`${apiUrlMembers}/csv`, fileName, "csv", {
    ...apiParams,
  });
};

export const getAdminMembers = async ({
  organizationId,
}: {
  organizationId: string;
}): Promise<MemberRaw[]> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ items: MemberRaw[] }>(
    `${apiUrlMembers}`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.items;
};
export const getAdminMember = async ({
  organizationId,
  uid,
}: {
  organizationId: string;
  uid: MemberRaw["uid"];
}): Promise<MemberRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ item: MemberRaw }>(
    `${apiUrlMembers}/${uid}`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};
export const postAdminAddMember = async ({
  organizationId,
  memberInvitationBody,
}: {
  organizationId: string;
  memberInvitationBody: MemberInvitationBody;
}): Promise<MemberAddResponse> => {
  const { apiOrganizationIdParams, apiHeadersWithJson } =
    getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<MemberAddResponse>(
    `${apiUrlMembers}`,
    memberInvitationBody,
    {
      ...apiHeadersWithJson,
      ...apiOrganizationIdParams,
    }
  );
  return {
    item: response.data.item,
    user_existed: response.data.user_existed,
  };
};
export const postAdminEditMember = async ({
  organizationId,
  memberUpdateForm,
}: {
  organizationId: string;
  memberUpdateForm: MemberUpdateForm;
}): Promise<MemberRaw> => {
  const { apiOrganizationIdParams, apiHeadersWithJson } =
    getAPIConfigs(organizationId);
  const userId = memberUpdateForm.is_new_user
    ? memberUpdateForm.idp_uid
    : memberUpdateForm.uid;
  const response = await API_CLIENT.post<{ item: MemberRaw }>(
    `${apiUrlMembers}/${userId}`,
    memberUpdateForm,
    {
      ...apiHeadersWithJson,
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};
export const postAdminDeleteMember = async ({
  organizationId,
  uid,
}: {
  organizationId: string;
  uid: MemberRaw["uid"];
}): Promise<MemberRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: MemberRaw }>(
    `${apiUrlMembers}/${uid}/delete`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};
export const postAdminEnableMember = async ({
  organizationId,
  uid,
}: {
  organizationId: string;
  uid: MemberRaw["uid"];
}): Promise<MemberRaw> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.post<{ item: MemberRaw }>(
    `${apiUrlMembers}/${uid}/enable`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.item;
};
export const postAdminRemoveMember = async ({
  organizationId,
  uid,
}: {
  organizationId: string;
  uid: MemberRaw["uid"];
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  await API_CLIENT.post(`${apiUrlMembers}/${uid}/remove`, undefined, {
    ...apiOrganizationIdParams,
  });
};

export const getAdminInvitedMembers = async ({
  organizationId,
}: {
  organizationId: string;
}): Promise<MemberRaw[]> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const response = await API_CLIENT.get<{ items: MemberRaw[] }>(
    `${apiUrlInvitedMembers}`,
    {
      ...apiOrganizationIdParams,
    }
  );
  return response.data.items;
};
export const postAdminEditInvitedMember = async ({
  organizationId,
  memberUpdateForm,
}: {
  organizationId: string;
  memberUpdateForm: MemberUpdateForm;
}): Promise<void> => {
  const userId = getMemberUserId({
    member: memberUpdateForm,
    isParsedIdpUid: false,
  });
  const { apiOrganizationIdParams, apiHeadersWithJson } =
    getAPIConfigs(organizationId);
  await API_CLIENT.post<{ item: MemberRaw }>(
    `${apiUrlInvitedMembers}/${userId}`,
    memberUpdateForm,
    {
      ...apiHeadersWithJson,
      ...apiOrganizationIdParams,
    }
  );
};
export const postAdminRemoveInvitedMember = async ({
  organizationId,
  member,
}: {
  organizationId: string;
  member: MemberRaw;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const userId = getMemberUserId({
    member: member,
    isParsedIdpUid: false,
  });
  await API_CLIENT.post(`${apiUrlInvitedMembers}/${userId}/remove`, undefined, {
    ...apiOrganizationIdParams,
  });
};
export const postAdminReinviteMember = async ({
  organizationId,
  member,
}: {
  organizationId: string;
  member: MemberRaw;
}): Promise<void> => {
  const { apiOrganizationIdParams } = getAPIConfigs(organizationId);
  const userId = getMemberUserId({
    member: member,
    isParsedIdpUid: false,
  });
  await API_CLIENT.post<{ item: MemberRaw }>(
    `${apiUrlInvitedMembers}/${userId}/reinvite`,
    undefined,
    {
      ...apiOrganizationIdParams,
    }
  );
};
